/**
 * Prepares the payment embed payload for pay-with-new-card checkout.
 *
 * @returns {Promise<string[]|null>}
 *   - `['card']` when the card should be saved with the payment
 *   - `[]` when paying with a one-time token (`customCardToken` on paymentModel)
 *   - `null` when validation or Stripe submission fails, or when `elements` is missing (caller should abort persist)
 */
export async function prepareCheckoutNewCardPaymentEmbed({
  elements,
  instrument,
  paymentModel,
  stripe,
}) {
  if (!elements) {
    return null;
  }

  const isReady = await validateNewCardPaymentSubmission({
    elements,
    instrument,
  });

  if (!isReady) {
    return null;
  }

  return embedForNewCardPaymentInstrument({
    elements,
    instrument,
    paymentModel,
    stripe,
  });
}

async function validateNewCardPaymentSubmission({ elements, instrument }) {
  const { error } = await elements.submit();

  instrument.set('_isDirty', true);
  await instrument.validate();

  return !error;
}

async function embedForNewCardPaymentInstrument({ elements, instrument, paymentModel, stripe }) {
  if (!instrument.name?.trim()) {
    return null;
  }

  const { paymentMethod } = await stripe.createPaymentMethod({
    elements,
    params: {
      /* eslint-disable camelcase */
      billing_details: {
        name: instrument.name,
      },
    },
  });
  const { card } = paymentMethod;

  instrument.setProperties({
    brand: card.brand,
    last4: card.last4,
    expMonth: card.exp_month,
    expYear: card.exp_year,
    paymentMethodId: paymentMethod.id,
  });

  if (instrument._saveCard) {
    return ['card'];
  }

  paymentModel.set('customCardToken', paymentMethod.id);

  return [];
}
